import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { useBalance, useIsLoggedIn, useSocket, useUser } from "@/hooks";
import { type WalletUpdateReason, applyWalletPush } from "@/lib/wallet";
import { WALLET_UNIT } from "@/lib/wallet/show";
import { type CurrencyChange, onCurrencyChange } from "@/lib/wallet/sync";
import { getBalance } from "@/services";
import { dispatch, updateBalance, updateUser } from "@/store";
import { handleError } from "@/utils";
import { PLAYER_WALLETS_QUERY_KEY } from "@/views/Profile/Wallet/data";

interface WalletPush {
  currency?: string;
  balance?: number;
  locked?: number;
  bonus?: number;
  baseCurrency?: string;
  reason?: WalletUpdateReason;
}

// Renders nothing. Mounted once, app-wide, so every page and tab follows balance and currency
// pushes, not only pages that show the header wallet. Must sit inside SocketProvider.
export default function WalletSync() {
  const isLoggedIn = useIsLoggedIn();
  const user = useUser();
  const balance = useBalance();
  const socket = useSocket();
  const queryClient = useQueryClient();

  const currency = user?.currency || "";
  const baseCurrency = user?.baseCurrency || "";
  // A push names the wallet unit, not the show currency.
  const unitRef = useRef(WALLET_UNIT);
  unitRef.current = balance?.currency || WALLET_UNIT;
  const userRef = useRef({ currency, baseCurrency });
  userRef.current = { currency, baseCurrency };

  const loadBalance = useCallback(async () => {
    try {
      dispatch(updateBalance(await getBalance()));
    } catch (error) {
      handleError(error);
    }
  }, []);

  // The show quote comes only with GET /api/balance, so any currency change re-reads it.
  useEffect(() => {
    if (isLoggedIn) loadBalance();
  }, [isLoggedIn, loadBalance, currency, baseCurrency]);

  const followCurrency = useCallback(
    (next: CurrencyChange) => {
      const now = userRef.current;
      const patch: CurrencyChange = {};
      if (next.currency && next.currency !== now.currency) patch.currency = next.currency;
      if (next.baseCurrency && next.baseCurrency !== now.baseCurrency) {
        patch.baseCurrency = next.baseCurrency;
      }
      if (!patch.currency && !patch.baseCurrency) return;
      dispatch(updateUser(patch));
      queryClient.invalidateQueries({ queryKey: PLAYER_WALLETS_QUERY_KEY });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!isLoggedIn) return;
    return onCurrencyChange(followCurrency);
  }, [isLoggedIn, followCurrency]);

  useEffect(() => {
    if (!socket) return;

    const handleWalletUpdate = (data: WalletPush) => {
      if (typeof data?.balance !== "number") return;
      if (data.currency && unitRef.current && data.currency !== unitRef.current) return;
      // A deposit in another coin moved the active row; follow it.
      if (data.baseCurrency) followCurrency({ baseCurrency: data.baseCurrency });

      applyWalletPush(
        {
          currency: data.currency || unitRef.current,
          ...(data.baseCurrency ? { baseCurrency: data.baseCurrency } : {}),
          balance: data.balance,
          locked: data.locked,
          bonus: data.bonus,
        },
        data.reason,
      );
    };
    // Pushes sent while the socket was down are lost; the server's figure replaces them.
    const handleReconnect = () => void loadBalance();

    socket.on("wallet:update", handleWalletUpdate);
    socket.on("user:currency", followCurrency);
    socket.io.on("reconnect", handleReconnect);
    return () => {
      socket.off("wallet:update", handleWalletUpdate);
      socket.off("user:currency", followCurrency);
      socket.io.off("reconnect", handleReconnect);
    };
  }, [socket, followCurrency, loadBalance]);

  return null;
}
